import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable } from '../../components/tables/DataTable';
import { Pagination, PAGINATION_BAR_HEIGHT } from '../../components/tables/Pagination';
import { ColumnSelector } from '../../components/tables/ColumnSelector';
import { useColumnPreferences } from '../../hooks/useColumnPreferences';
import { PAGINATION_DEFAULT_PAGE_SIZE } from '../../constants/config';
import { ROUTES } from '../../constants/routes';
import { getAllWorkOrders, getWorkOrderStatusCounts } from '../../services/workOrdersTabService';
import { getClients } from '../../services/clientService';
import { getProjects } from '../../services/projectService';
import type { Client, Project } from '../../types/project';
import type { WorkOrderTabRow, WorkOrdersTabFilters } from '../../types/workOrdersTab';
import { WorkOrdersFilters } from './WorkOrdersFilters';
import { NewWorkOrderDialog } from './NewWorkOrderDialog';
import { workOrdersTabColumns } from './workOrdersTabColumns';
import { formatWorkOrderTableData } from './workOrdersTabHelpers';
import { useWorkOrderRowDelete } from './useWorkOrderRowDelete';
import { usePermissions } from '../../hooks/usePermissions';

const DEFAULT_VISIBLE = [
  'WorkOrderId', 'WorkOrderName', 'ProjectName', 'ClientName', 'WorkOrderStatus', 'FromQuoteNumber',
  'Schedule', 'SampleCount', 'WorkOrderStartDate', 'Progress', 'sysLastModifiedDate',
];
const MANDATORY_FIELDS = ['WorkOrderId', 'WorkOrderStatus'];

const DEFAULT_FILTERS: WorkOrdersTabFilters = {
  statuses: ['Scheduled', 'In Progress'],
  projectKey: null,
  clientKey: null,
  labLocation: null,
  startDateFrom: '',
  startDateTo: '',
};

const buildPageTitle = (statuses: string[]): string => {
  if (statuses.length === 0) return 'All Work Orders';
  if (statuses.length === 1) return `${statuses[0]} Work Orders`;
  if (
    statuses.length === 2 &&
    statuses.includes('Scheduled') &&
    statuses.includes('In Progress')
  ) {
    return 'Active Work Orders';
  }
  return 'Work Orders';
};

export const WorkOrdersTabPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // canDeleteWorkOrder is the same admin-level gate the Projects page row
  // delete uses (canManageEntities) — the backend route is requireRole('admin').
  const { canCreateWorkOrder, canDeleteWorkOrder } = usePermissions();

  const [data, setData] = useState<WorkOrderTabRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState('sysLastModifiedDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const urlStatus = searchParams.get('status');
  const [filters, setFilters] = useState<WorkOrdersTabFilters>(
    urlStatus
      ? { ...DEFAULT_FILTERS, statuses: urlStatus.split(',') }
      : DEFAULT_FILTERS,
  );
  const [search, setSearch] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [dialogOpen, setDialogOpen] = useState(searchParams.get('newWorkOrder') === '1');

  useEffect(() => {
    getClients().then(setClients).catch(() => {});
    getProjects({ pageSize: 9999 }).then((r) => setProjects(r.data)).catch(() => {});
    getWorkOrderStatusCounts().then(setStatusCounts).catch(() => {});
  }, []);

  const fetchWorkOrders = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filters.statuses.length > 0 ? filters.statuses.join(',') : undefined;
      const result = await getAllWorkOrders({
        page,
        pageSize,
        sortBy,
        sortDir,
        status: statusParam,
        projectKey: filters.projectKey ?? undefined,
        clientKey: filters.clientKey ?? undefined,
        labLocation: filters.labLocation ?? undefined,
        startDateFrom: filters.startDateFrom || undefined,
        startDateTo: filters.startDateTo || undefined,
        search: search || undefined,
      });
      setData(result.data);
      setTotal(result.total);
    } catch {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortDir, filters, search]);

  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  // Row delete (2026-09-11): trash icon as the last column, Projects-page
  // pattern; confirm via the hardened WoDeleteDialog; refetch + counts after.
  const { handleDeleteClick, dialog: deleteDialog } = useWorkOrderRowDelete({
    onDeleted: () => {
      fetchWorkOrders();
      getWorkOrderStatusCounts().then(setStatusCounts).catch(() => {});
    },
  });

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const handleRowClick = (row: Record<string, unknown>) => {
    const projectId = row.ProjectId as string;
    const workOrderKey = row.WorkOrderKey as string;
    navigate(
      ROUTES.WORK_ORDER_DETAIL
        .replace(':projectId', projectId)
        .replace(':workOrderId', workOrderKey) + '?from=tab',
    );
  };

  const handleFilterChange = (next: WorkOrdersTabFilters) => {
    setFilters(next);
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearch(value);
      setPage(0);
    }, 300);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  const handleWorkOrderCreated = (wo: { WorkOrderKey: string; ProjectId: string }) => {
    setDialogOpen(false);
    navigate(
      ROUTES.WORK_ORDER_DETAIL
        .replace(':projectId', wo.ProjectId)
        .replace(':workOrderId', wo.WorkOrderKey),
    );
  };

  const { visibleFields, columnVisibility, toggleColumn, resetDefaults } =
    useColumnPreferences('workorders-tab', workOrdersTabColumns, DEFAULT_VISIBLE, MANDATORY_FIELDS);
  const filteredColumns = workOrdersTabColumns.filter((c) => visibleFields.includes(c.field));

  const pageTitle = useMemo(() => buildPageTitle(filters.statuses), [filters.statuses]);

  const projectOptions = projects.map((p) => ({
    ProjectKey: p.ProjectKey,
    ProjectName: p.ProjectName,
    ProjectId: p.ProjectId,
  }));

  const tableData = useMemo(() => formatWorkOrderTableData(data), [data]);

  return (
    <Box sx={{ pt: 1, pb: 3 }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: 'text.primary',
            letterSpacing: '-0.025em',
            fontSize: '1.75rem',
            lineHeight: 1.2,
          }}
        >
          {pageTitle}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search work orders..."
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 240,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: '0.8125rem',
            },
          }}
        />
        <ColumnSelector columns={columnVisibility} onToggle={toggleColumn} onReset={resetDefaults} />
        {/* Admin-only: backend route (POST /workorders/standalone) is gated
            with requireRole('admin'), so non-admins would 403 anyway — don't
            offer the affordance. See BUG-2026-0013. */}
        {canCreateWorkOrder && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
            size="small"
            sx={{
              boxShadow: '0 2px 8px rgba(74,103,65,0.2)',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 0.75,
              fontSize: '0.8125rem',
              letterSpacing: '-0.005em',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 14px rgba(74,103,65,0.3)',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            New Work Order
          </Button>
        )}
      </Box>

      {/* Content: filters + table */}
      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
        <WorkOrdersFilters
          projects={projectOptions}
          clients={clients}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClear={handleClearFilters}
          statusCounts={statusCounts}
        />

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <DataTable
            columns={filteredColumns}
            data={tableData}
            loading={loading}
            onRowClick={handleRowClick}
            onDeleteClick={canDeleteWorkOrder ? handleDeleteClick : undefined}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            resizable
            storageKey="workorders-tab"
            stickyHeader
            bottomReserve={PAGINATION_BAR_HEIGHT}
          />

          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            entityLabel="work orders"
          />
        </Box>
      </Box>

      <NewWorkOrderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={handleWorkOrderCreated}
        projects={projectOptions}
      />
      {deleteDialog}
    </Box>
  );
};

export default WorkOrdersTabPage;
